import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Path, { ERROR_MESSAGE_TRANSITION } from 'constants/routePath';
import { messageModalActions } from 'slices/messageModalSlice';
import { actions as loginActions } from 'slices/loginSlice';
import { useQueryParams } from './useQueryParams';
import { decode } from 'utils/base64Util';
import { mobilaActions } from 'slices/mobilaSlice';
import { ecFlowActions } from 'slices/ecFlowSlice';
import { isEmpty } from 'utils/stringUtil';
import {
  EC_PREFIX,
  EC_USE_PARAMS,
  EC_MODIFY_REQUIRED_PARAMS,
  EC_CALLBACK_URL_PARAM,
  isValidResvYmd,
  isValidCallbackUrl,
  hasRequiredEcParams,
} from 'utils/ecTransitionUtil';

/**
 * 外部遷移の引数から遷移先情報を取り出すhooks
 */
export const useExternalTransition = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { accountInfo } = useSelector(state => state.account);
  const queryParams = useQueryParams();
  // restには、'p3','p4','p5','p13'+期待しないパラメータが含まれる
  const {
    p1,
    p2,
    p6,
    p7,
    p8,
    p9,
    p10,
    p11,
    p12,
    st,
    bcd,
    sid,
    v1,
    // EC購入導線固有パラメータ（ec*）
    ecOrderNo,
    ecMeisaiNo,
    ecResvStartYmd,
    ecChubunCd,
    ecShobunCd,
    ecTokCd,
    ecCarNo,
    ecYykNo,
    // EC戻り遷移の宛先URL（ec 接頭辞は付かない。EC側命名に合わせる）
    callbackUrl,
    ...rest
  } = queryParams;
  const prefix = ['p3', 'p4', 'p5', 'p13'];

  // ec* が付与されているか（前段ガード・エラー分岐の判定に使用）
  const hasEcParam = Object.keys(queryParams).some(k =>
    k.startsWith(EC_PREFIX)
  );
  // ec* 利用時は p1 を付与しないため、ec* 時は ecOrderNo、従来は p1 で判定
  const isEcRoute = ecOrderNo || (p1 && p1 !== 'MOBILA');
  // react-router には canGoBack() が無いため、初期エントリ以外（SPA内部遷移）に加え、
  // リンク経由の遷移（document.referrer が非空）かつブラウザの閲覧履歴が2件以上ある場合
  // （＝フルナビゲーションでも実際に戻れる前画面が存在する場合）も「戻り先あり」とみなす。
  // window.history.length のみで判定すると、アドレスバー直接入力・新規タブでの直接遷移でも
  // ブラウザが内部的に積む履歴エントリを「戻り先あり」と誤判定してしまうため、
  // リンク経由（referrer 非空）であることも合わせて要求する
  const canGoBack =
    location.key !== 'default' ||
    (document.referrer !== '' && window.history.length > 1);

  // パラメータに引数のkey以外が含まれているか判定する
  const hasUnmatchKey = (params, keys) => {
    const allKeys = Object.keys(params);
    for (const key of allKeys) {
      if (prefix.filter(p => key.startsWith(p)).length > 0) {
        // 在庫引当用のパラメータ確認処理
        for (const p of prefix) {
          if (key.startsWith(p) && !keys.includes(p)) {
            return true;
          }
        }
      } else if (!keys.includes(key)) {
        return true;
      }
    }
    return false;
  };

  // パラメータ不正エラーを表示する
  const showError = () => {
    const message = ERROR_MESSAGE_TRANSITION;
    dispatch(messageModalActions.showMessageModal({ message }));
    dispatch(loginActions.closeAbidDifferenceModal());
  };

  // EC動線コンテキストを保持する（完了画面の仮予約情報連携で参照／§11）
  // ※後続の検証（hasUnmatchKey・日付書式・必須項目）が全て成功し、遷移先が
  //   確定した後にのみ呼び出すこと。検証失敗時に呼ぶと、fromEc:true が
  //   sessionStorage（ecFlowSlice参照）に残留してしまう
  const dispatchEcFlow = () => {
    dispatch(
      ecFlowActions.setEcFlow({
        fromEc: true,
        orderNo: ecOrderNo ?? p1,
        tokCd: ecTokCd ?? p2,
        carNo: ecCarNo ?? p8,
        meisaiNo: ecMeisaiNo ?? null,
        callbackUrl,
        // 開始時刻はここで確定させる（reducer側での採番だと呼び出しタイミングに
        // 依存し非決定的になるため）。同一動線への再dispatch時に維持するかどうかは
        // ecFlowSlice.setEcFlow 側の判定に委ねる
        startedAt: Date.now(),
      })
    );
  };

  // パラメータ不正時のエラー処理（ec* 有無で分岐）
  // ・ec* 経由 : モーダル表示＋前画面へ戻す（navigate(-1)）。戻り先が無ければ遷移せずモーダルのまま
  // ・p* のみ  : 従来どおり TOP
  const onParamError = () => {
    showError();
    dispatch(ecFlowActions.clearEcFlow());
    if (hasEcParam) {
      return canGoBack ? { back: true } : { to: null };
    }
    return { to: Path.TOP };
  };

  // エラーチェックを行い、遷移パラメータを作成する
  const createLink = (to, useParams, extraState) => {
    if (hasUnmatchKey(queryParams, useParams)) {
      return onParamError();
    }
    // EC固有: ecResvStartYmd が指定されている場合のみ書式・実在日を検証（fail-fast）
    if (ecResvStartYmd && !isValidResvYmd(ecResvStartYmd)) {
      return onParamError();
    }
    // EC固有: callbackUrl が指定されている場合のみ書式・ドメインを検証（fail-fast）
    if (callbackUrl && !isValidCallbackUrl(callbackUrl)) {
      return onParamError();
    }
    return {
      to,
      state: {
        ...location.state,
        ...extraState,
        queryParams: { ...queryParams, p12: decode(p12) },
      },
    };
  };

  // 外部遷移チェック
  const createRedirect = () => {
    // queryParamsから必要なパラメータ以外を排除
    for (const param in rest) {
      if (
        prefix.filter(p => param.startsWith(p)).length === 0 &&
        !param.startsWith(EC_PREFIX) &&
        param !== EC_CALLBACK_URL_PARAM
      ) {
        delete queryParams[param];
      }
    }

    // CLPからの遷移の場合、外部遷移パラメータがないこともあるので、外部戦かどうか判断する前に、MOBILAフラグを確認する
    if (
      window.localStorage.getItem('isMobila') === true ||
      window.localStorage.getItem('isMobila') === 'true'
    ) {
      dispatch(mobilaActions.setMobila(true));
      window.localStorage.removeItem('isMobila');
    }

    // クエリパラメータがない場合、外部遷移ではない
    if (Object.keys(queryParams).length === 0) return null;

    // MOBILA経由か
    if (p1) {
      dispatch(mobilaActions.setMobila(p1 === 'MOBILA'));
    }

    // ec* 付与時は EC購入導線以外への遷移口を封鎖（分岐判定より前段／R5）
    if (hasEcParam && !isEcRoute) {
      return onParamError();
    }

    if (v1) {
      // オイル交換・タイヤ交換のLPページ
      return createLink(Path.RESERVATION_SERVICE_SELECT, ['v1', 'p10']);
    } else if (p1 && p1 === 'MOBILA') {
      // MOBILA経由遷移
      return createLink(accountInfo ? Path.TOP : 'TODO', ['p1', 'p12']);
    } else if (p1 && p1 !== 'MOBILA') {
      // 店舗在庫引当経由遷移
      return createLink(Path.RESERVATION_DATE_SELECT, [
        'p1',
        'p2',
        'p3',
        'p4',
        'p5',
        'p6',
        'p7',
        'p8',
        'p9',
        'p10',
        'p11',
        'p12',
        'p13',
      ]);
    } else if (isEcRoute) {
      // 店舗在庫引当経由遷移（EC購入導線）※ec* 時は ecOrderNo、従来は p1 で判定
      if (ecYykNo) {
        if (EC_MODIFY_REQUIRED_PARAMS.some(k => isEmpty(queryParams[k]))) {
          return onParamError();
        }
        const link = createLink(
          Path.RESERVATION_DATE_SELECT,
          [...EC_USE_PARAMS, 'ecYykNo'],
          { baseScreen: Path.RESERVATION_LIST }
        );
        // 検証成功（遷移先確定）時のみEC動線コンテキストを保持する
        if (link?.to) {
          dispatchEcFlow();
        }
        return link;
      }

      // 初回予約 → 日時選択へ（外部遷移）
      if (
        !hasRequiredEcParams({
          ecOrderNo,
          ecChubunCd,
          ecShobunCd,
          ecTokCd,
        })
      ) {
        return onParamError();
      }
      const link = createLink(Path.RESERVATION_DATE_SELECT, EC_USE_PARAMS);
      // 検証成功（遷移先確定）時のみEC動線コンテキストを保持する
      if (link?.to) {
        dispatchEcFlow();
      }
      return link;
    } else if (st || bcd) {
      // 店舗・法人・MK戦略（店舗/中分類指定）
      return createLink(Path.RESERVATION_SERVICE_SELECT, ['st', 'bcd']);
    } else if (sid) {
      // 店舗・法人・MK戦略（店舗/中分類/小分類指定）
      return createLink(Path.RESERVATION_DATE_SELECT, ['sid']);
    } else if (p2 && p10 && p11) {
      // 店舗/中分類/小分類指定
      return createLink(Path.RESERVATION_DATE_SELECT, [
        'p2',
        'p10',
        'p11',
        'p12',
      ]);
    } else if (p8 && p10) {
      // 車両連番/中分類/小分類指定
      return createLink(Path.RESERVATION_SERVICE_SELECT, ['p8', 'p10', 'p11']);
    } else if (p10 && p11) {
      // 中分類/小分類指定
      return createLink(Path.RESERVATION_SHOP_SELECT, ['p10', 'p11', 'p12']);
    } else {
      // 上記パターン以外 ※パラメータ不正もここに来る可能性あり
      return createLink(Path.RESERVATION_SERVICE_SELECT, [
        'p2',
        'p10',
        'p11',
        'p12',
      ]);
    }
  };

  return { redirect: createRedirect() };
};
